// The provider's day as one strip, for the Quick Schedule.
//
// The dialog showed only the open chips, so staff could not see why 12:00
// was missing or what sat either side of 1:30, while the calendar behind it
// knew. The slot engine already names every half hour of the working day
// and why it is not open; the day's appointments say who is in it. This
// turns both into rows: open ones to tap, and everything else named — the
// patient, a hold, the lunch gap, a past time, a half hour too close to the
// end of a period for this visit's length.
import { quickSlotLabel } from './quickSlotLabel';

export interface StripSlot {
  time: string;
  available: boolean;
  unavailableReason?: string | null;
  duration?: number;
  [key: string]: unknown;
}

/** An appointment on the day, in the clinic's clock. */
export interface StripAppointment { start: string; end: string; patientName?: string | null; }

export type StripState = 'open' | 'booked' | 'held' | 'past' | 'tight' | 'off';

export interface StripRow {
  start: string;
  end: string;
  state: StripState;
  label: string;
  /** Present on open rows: the slot to book. */
  slot?: StripSlot;
}

const STEP = 30;

const minutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};
const clock = (total: number): string => `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;

const LABELS = {
  held: 'Held for a patient',
  past: 'Past',
  break: 'Not enough time before the break',
  next: 'Too close to the next appointment',
  off: 'Off',
};

// The appointment somebody is in at this half hour, if the list carries one.
const inside = (start: string, appointments: StripAppointment[]): StripAppointment | undefined => {
  const at = minutes(start);
  return appointments.find((a) => minutes(a.start) <= at && at < minutes(a.end));
};

// The engine says "overlaps an existing appointment" both for a half hour
// somebody is in and for one where a visit of this length would run into the
// next booking. Only the first is "booked"; the second is "too close".
const read = (slot: StripSlot, appointments: StripAppointment[]): { state: StripState; label: string } => {
  if (slot.available) return { state: 'open', label: quickSlotLabel(slot as any) };
  const why = String(slot.unavailableReason || '');
  if (/held/.test(why)) return { state: 'held', label: LABELS.held };
  if (/passed/.test(why)) return { state: 'past', label: LABELS.past };
  if (/run past the end/.test(why)) return { state: 'tight', label: LABELS.break };
  const who = inside(slot.time, appointments);
  if (who) return { state: 'booked', label: String(who.patientName || '').trim() || 'Booked' };
  return { state: 'tight', label: LABELS.next };
};

export function dayStrip(slots: StripSlot[], appointments: StripAppointment[]): StripRow[] {
  const ordered = [...slots].sort((a, b) => minutes(a.time) - minutes(b.time));
  const rows: StripRow[] = [];
  let previousEnd: number | null = null;
  ordered.forEach((slot) => {
    const start = minutes(slot.time);
    // A gap between two periods — lunch, or a break — is a row of its own.
    if (previousEnd !== null && start > previousEnd) {
      rows.push({ start: clock(previousEnd), end: clock(start), state: 'off', label: LABELS.off });
    }
    const { state, label } = read(slot, appointments);
    const end = start + STEP;
    if (state === 'open') {
      rows.push({ start: slot.time, end: clock(end), state, label, slot });
    } else {
      const last = rows[rows.length - 1];
      // Consecutive half hours of the same kind read as one block.
      if (last && last.state === state && last.label === label && minutes(last.end) === start) {
        last.end = clock(end);
      } else {
        rows.push({ start: slot.time, end: clock(end), state, label });
      }
    }
    previousEnd = end;
  });
  return rows;
}
